import { SDK_DEFAULTS } from "@watchover/shared";

export interface WatchOverConfig {
  /** Project write key (required). */
  key: string;
  /** Ingest collector base URL, e.g. https://ingest.watchover.dev */
  ingestUrl: string;
  /** 0..1 — fraction of sessions to record at all. */
  sampleRate: number;
  /** Record DOM for session replay. */
  replay: boolean;
  /** Autocapture clicks/inputs/scroll/navigation without manual instrumentation. */
  autocapture: boolean;
  /** Capture window.onerror / unhandledrejection. */
  captureErrors: boolean;
  /** Capture Core Web Vitals (LCP/CLS/INP/FCP/TTFB). */
  captureVitals: boolean;
  /** CSS selectors whose text + input values are redacted before sending. */
  maskSelectors: string[];
  /** Inputs matching these selectors send their raw value (opt back in). */
  unmaskSelectors: string[];
  /** Flush cadence + batch thresholds. */
  flushIntervalMs: number;
  maxBatchEvents: number;
  maxBatchBytes: number;
  /** Inactivity before the session id rotates. */
  sessionTimeoutMs: number;
  /** Share visitor + session across subdomains via a root-domain cookie. */
  crossSubdomain: boolean;
  /** Force the cookie domain (e.g. "jetvisa.io"). Empty = auto-detect. */
  cookieDomain: string;
  /** Honour the browser's Do-Not-Track / Global-Privacy-Control signal: when
   *  the visitor has it on, don't record and don't set identifiers. */
  respectDnt: boolean;
  /** Print debug logs to the console. */
  debug: boolean;
}

const truthy = (v: string | null | undefined) =>
  v === "" || v === "true" || v === "1" || v === "yes";

/**
 * Resolve config from (in order of precedence):
 *   1. explicit object passed to watchover.init(config)
 *   2. window.watchoverConfig
 *   3. data-* attributes on the loading <script> tag
 *   4. built-in defaults
 */
export function resolveConfig(
  override?: Partial<WatchOverConfig>,
): WatchOverConfig | null {
  const fromScript = readScriptTag();
  const fromGlobal = (window as any).watchoverConfig as
    | Partial<WatchOverConfig>
    | undefined;

  const key = override?.key ?? fromGlobal?.key ?? fromScript.key;
  if (!key) {
    console.warn("[watchover] missing project key — tracker not started");
    return null;
  }

  const ingestUrl =
    override?.ingestUrl ??
    fromGlobal?.ingestUrl ??
    fromScript.ingestUrl ??
    deriveIngestFromScript() ??
    "";

  if (!ingestUrl) {
    console.warn("[watchover] could not determine ingest URL");
    return null;
  }

  const merged: WatchOverConfig = {
    key,
    ingestUrl: ingestUrl.replace(/\/+$/, ""),
    sampleRate: num(override?.sampleRate ?? fromGlobal?.sampleRate ?? fromScript.sampleRate, SDK_DEFAULTS.sampleRate),
    replay: bool(override?.replay ?? fromGlobal?.replay ?? fromScript.replay, true),
    autocapture: bool(override?.autocapture ?? fromGlobal?.autocapture ?? fromScript.autocapture, true),
    captureErrors: bool(override?.captureErrors ?? fromGlobal?.captureErrors, true),
    captureVitals: bool(override?.captureVitals ?? fromGlobal?.captureVitals, true),
    maskSelectors: arr(override?.maskSelectors ?? fromGlobal?.maskSelectors ?? fromScript.maskSelectors),
    unmaskSelectors: arr(override?.unmaskSelectors ?? fromGlobal?.unmaskSelectors),
    flushIntervalMs: num(override?.flushIntervalMs ?? fromGlobal?.flushIntervalMs, SDK_DEFAULTS.flushIntervalMs),
    maxBatchEvents: num(override?.maxBatchEvents ?? fromGlobal?.maxBatchEvents, SDK_DEFAULTS.maxBatchEvents),
    maxBatchBytes: num(override?.maxBatchBytes ?? fromGlobal?.maxBatchBytes, SDK_DEFAULTS.maxBatchBytes),
    sessionTimeoutMs: num(override?.sessionTimeoutMs ?? fromGlobal?.sessionTimeoutMs, SDK_DEFAULTS.sessionTimeoutMs),
    // Default OFF: sharing identity across subdomains leaks the visitor/user id
    // to unrelated subdomains; multi-tenant domains must opt in explicitly.
    crossSubdomain: bool(override?.crossSubdomain ?? fromGlobal?.crossSubdomain ?? fromScript.crossSubdomain, false),
    cookieDomain: (override?.cookieDomain ?? fromGlobal?.cookieDomain ?? fromScript.cookieDomain ?? "").replace(/^\./, ""),
    respectDnt: bool(override?.respectDnt ?? fromGlobal?.respectDnt ?? fromScript.respectDnt, true),
    debug: bool(override?.debug ?? fromGlobal?.debug ?? fromScript.debug, false),
  };
  return merged;
}

function num(v: unknown, d: number): number {
  const n = typeof v === "string" ? parseFloat(v) : (v as number);
  return Number.isFinite(n) ? n : d;
}
function bool(v: unknown, d: boolean): boolean {
  if (v === undefined || v === null) return d;
  if (typeof v === "boolean") return v;
  return truthy(String(v));
}
function arr(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[];
  if (typeof v === "string" && v.trim())
    return v.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
}

interface ScriptConfig {
  key?: string;
  ingestUrl?: string;
  sampleRate?: string;
  replay?: string;
  autocapture?: string;
  maskSelectors?: string;
  crossSubdomain?: string;
  cookieDomain?: string;
  respectDnt?: string;
  debug?: string;
}

function currentScript(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement)
    return document.currentScript;
  // Fallback: the last script tag whose src is actually the SDK bundle. Match on
  // the path ending in /watchover.js (not a loose substring) so an injected
  // <script src="…/watchover.js.evil"> can't hijack config derivation.
  const scripts = document.getElementsByTagName("script");
  for (let i = scripts.length - 1; i >= 0; i--) {
    const src = scripts[i]!.src || "";
    try {
      if (new URL(src, location.href).pathname.endsWith("/watchover.js"))
        return scripts[i]!;
    } catch {
      /* ignore unparseable src */
    }
  }
  return null;
}

function readScriptTag(): ScriptConfig {
  const el = currentScript();
  if (!el) return {};
  const d = el.dataset;
  return {
    key: d.key,
    ingestUrl: d.ingest,
    sampleRate: d.sample,
    replay: d.replay,
    autocapture: d.autocapture,
    maskSelectors: d.mask,
    crossSubdomain: d.crossSubdomain,
    cookieDomain: d.cookieDomain,
    respectDnt: d.respectDnt,
    debug: d.debug,
  };
}

/** If no ingest URL is given, assume the collector is the script's own origin. */
function deriveIngestFromScript(): string | null {
  const el = currentScript();
  if (!el?.src) return null;
  try {
    return new URL(el.src).origin;
  } catch {
    return null;
  }
}
